import React, { useState } from "react";
import { useTimeAnalytics } from "../../../hooks/useDemandAnalytics";
import BarChart from "../../../components/admin/BarChart";
import StatCard from "../../../components/admin/StatCard";
import {
  BarChart as ReBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
} from "recharts";

const TimeAnalyticsTab = () => {
  const [days, setDays] = useState(90);
  const { data, isLoading } = useTimeAnalytics(days);

  if (isLoading) {
    return <div className="skeleton h-64 rounded-lg"></div>;
  }

  const d = data?.data;
  if (!d) return <div className="text-gray-500">No data available</div>;

  const hourChartData = (d.byHour || []).map((h) => ({
    label: `${h.hour}:00`,
    value: h.count,
  }));

  const dayChartData = (d.byDayOfWeek || []).map((d) => ({
    label: d.dayName?.slice(0, 3) || d.day,
    value: d.count,
  }));

  const monthChartData = (d.byMonth || []).map((m) => ({
    month: m.month,
    count: m.count,
  }));

  const weekChartData = (d.byWeek || []).map((w) => ({
    week: w.week?.slice(-5) || w.week,
    count: w.count,
  }));

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium">Period:</span>
        <select
          className="select select-bordered select-sm"
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
        >
          <option value={30}>Last 30 days</option>
          <option value={60}>Last 60 days</option>
          <option value={90}>Last 90 days</option>
          <option value={180}>Last 6 months</option>
          <option value={365}>Last year</option>
        </select>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <StatCard
          title="Peak Hours"
          value={d.peakHours?.length ? d.peakHours.map((h) => `${h}:00`).join(", ") : "N/A"}
          icon="fa-clock"
          color="bg-purple-50"
          textColor="text-purple-600"
        />
        <StatCard
          title="Peak Days"
          value={d.peakDays?.length ? d.peakDays.join(", ") : "N/A"}
          icon="fa-calendar"
          color="bg-blue-50"
          textColor="text-blue-600"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card bg-base-100 shadow">
          <div className="card-body">
            <h2 className="card-title text-sm">Requests by Hour of Day</h2>
            <BarChart
              data={hourChartData}
              labelKey="label"
              valueKey="value"
              color="bg-purple-500"
              height="h-40"
              showValues={false}
            />
          </div>
        </div>

        <div className="card bg-base-100 shadow">
          <div className="card-body">
            <h2 className="card-title text-sm">Requests by Day of Week</h2>
            <BarChart
              data={dayChartData}
              labelKey="label"
              valueKey="value"
              color="bg-blue-500"
              height="h-40"
            />
          </div>
        </div>
      </div>

      <div className="card bg-base-100 shadow">
        <div className="card-body">
          <h2 className="card-title text-sm">Monthly Trend</h2>
          {monthChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={monthChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Line type="monotone" dataKey="count" stroke="#ef4444" strokeWidth={2} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center text-gray-400 py-8">No monthly data</div>
          )}
        </div>
      </div>

      <div className="card bg-base-100 shadow">
        <div className="card-body">
          <h2 className="card-title text-sm">Weekly Trend (Last 24 Weeks)</h2>
          {weekChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <ReBarChart data={weekChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="week" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="count" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
              </ReBarChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center text-gray-400 py-8">No weekly data</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TimeAnalyticsTab;
